import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient'; 

export default function AdminPanel() {
  const [activeTab, setActiveTab] = useState('appointments');
  
  // Data States
  const [appointments, setAppointments] = useState([]);
  const [videos, setVideos] = useState([]);
  const [blogs, setBlogs] = useState([]);
  
  // Loading & Submission States
  const [loading, setLoading] = useState(false);
  const [isSubmittingVideo, setIsSubmittingVideo] = useState(false);
  const [isSubmittingBlog, setIsSubmittingBlog] = useState(false);

  // Content States
  const initialVideoState = { title: '', video_link: '', category: 'Therapy', platform: 'YouTube', display_order: 0 };
  const initialBlogState = { title: '', excerpt: '', content: '', image_url: '', display_order: 0 };
  
  const [newVideo, setNewVideo] = useState(initialVideoState);
  const [newBlog, setNewBlog] = useState(initialBlogState);

  // Edit Mode States
  const [editingVideoId, setEditingVideoId] = useState(null);
  const [editingBlogId, setEditingBlogId] = useState(null);

  // Search States
  const [searchAppt, setSearchAppt] = useState('');
  const [searchVideo, setSearchVideo] = useState('');
  const [searchBlog, setSearchBlog] = useState('');

  // Pagination States (Items per page = 5)
  const ITEMS_PER_PAGE = 5;
  const [pageAppt, setPageAppt] = useState(1);
  const [pageVideo, setPageVideo] = useState(1);
  const [pageBlog, setPageBlog] = useState(1);

  // Drag and Drop Refs
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  // UI Polish States
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [deleteModal, setDeleteModal] = useState({ show: false, table: '', id: '' });

  const videoFormRef = useRef(null);
  const blogFormRef = useRef(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  // Reset pagination when searching
  useEffect(() => { setPageAppt(1); }, [searchAppt]);
  useEffect(() => { setPageVideo(1); }, [searchVideo]);
  useEffect(() => { setPageBlog(1); }, [searchBlog]);

  // --- UI Helpers ---
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  // --- Data Fetching ---
  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([fetchAppointments(), fetchVideos(), fetchBlogs()]);
    setLoading(false);
  };

  async function fetchAppointments() {
    const { data } = await supabase.from('appointments').select('*').order('created_at', { ascending: false });
    if (data) setAppointments(data);
  }

  async function fetchVideos() {
    const { data } = await supabase.from('videos').select('*').order('display_order', { ascending: true }).order('created_at', { ascending: false });
    if (data) setVideos(data);
  }

  async function fetchBlogs() {
    const { data } = await supabase.from('blogs').select('*').order('display_order', { ascending: true }).order('created_at', { ascending: false });
    if (data) setBlogs(data);
  }

  // --- Handlers for Appointments ---
  const handleUpdateStatus = async (id, newStatus) => {
    const { error } = await supabase.from('appointments').update({ status: newStatus }).eq('id', id);
    if (!error) {
      showToast(`Inquiry marked as ${newStatus} ✨`);
      fetchAppointments();
    } else {
      showToast('Failed to update status.', 'error');
    }
  };

  // --- Handlers for Videos ---
  const handleAddOrUpdateVideo = async (e) => {
    e.preventDefault();
    setIsSubmittingVideo(true);
    
    if (editingVideoId) {
      const { error } = await supabase.from('videos').update(newVideo).eq('id', editingVideoId);
      if (!error) {
        showToast('Video Updated Successfully! ✨');
        setEditingVideoId(null);
      } else showToast('Error updating video.', 'error');
    } else {
      // Set display order to bottom of list
      const insertData = { ...newVideo, display_order: videos.length };
      const { error } = await supabase.from('videos').insert([insertData]);
      if (!error) showToast('Video is now Live! 🚀');
      else showToast('Error publishing video.', 'error');
    }
    
    setNewVideo(initialVideoState);
    fetchVideos();
    setIsSubmittingVideo(false);
  };

  const handleEditVideoClick = (video) => {
    setNewVideo(video);
    setEditingVideoId(video.id);
    videoFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const cancelVideoEdit = () => {
    setNewVideo(initialVideoState);
    setEditingVideoId(null);
  };

  // --- Handlers for Blogs ---
  const handleAddOrUpdateBlog = async (e) => {
    e.preventDefault();
    setIsSubmittingBlog(true);
    
    if (editingBlogId) {
      const { error } = await supabase.from('blogs').update(newBlog).eq('id', editingBlogId);
      if (!error) {
        showToast('Article Updated Successfully! ✨');
        setEditingBlogId(null);
      } else showToast('Error updating article.', 'error');
    } else {
      const insertData = { ...newBlog, display_order: blogs.length };
      const { error } = await supabase.from('blogs').insert([insertData]);
      if (!error) showToast('Blog Published! ✍️');
      else showToast('Error publishing blog.', 'error');
    }
    
    setNewBlog(initialBlogState);
    fetchBlogs();
    setIsSubmittingBlog(false);
  };

  const handleEditBlogClick = (blog) => {
    setNewBlog(blog);
    setEditingBlogId(blog.id);
    blogFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const cancelBlogEdit = () => {
    setNewBlog(initialBlogState);
    setEditingBlogId(null);
  };

  // --- Drag and Drop Sorting Logic ---
  const handleSortEnd = async (table, dataList) => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    if (dragItem.current === dragOverItem.current) return; // No change

    let _items = [...dataList];
    const draggedItemContent = _items.splice(dragItem.current, 1)[0];
    _items.splice(dragOverItem.current, 0, draggedItemContent);

    dragItem.current = null;
    dragOverItem.current = null;

    // Update local state immediately for snappy UI
    const updatedItems = _items.map((item, index) => ({ ...item, display_order: index }));
    if (table === 'videos') setVideos(updatedItems);
    if (table === 'blogs') setBlogs(updatedItems);

    // Bulk update Supabase silently
    await Promise.all(
      updatedItems.map(item => supabase.from(table).update({ display_order: item.display_order }).eq('id', item.id))
    );
    showToast('Display order saved! 📌');
  };

  // --- Delete Logic ---
  const requestDelete = (table, id) => {
    setDeleteModal({ show: true, table, id });
  };

  const executeDelete = async () => {
    const { table, id } = deleteModal;
    const { error } = await supabase.from(table).delete().eq('id', id);
    if (!error) {
      if (table === 'blogs') fetchBlogs();
      else if (table === 'videos') fetchVideos();
      else fetchAppointments();
      showToast('Item permanently deleted.', 'success');
    } else {
      showToast('Error deleting item.', 'error');
    }
    setDeleteModal({ show: false, table: '', id: '' });
  };

  const handleLogout = async () => { 
    await supabase.auth.signOut();
    window.location.href = "/"; 
  };

  // --- Filtering & Pagination Logic ---
  const filteredAppts = appointments.filter(a => 
    a.user_name.toLowerCase().includes(searchAppt.toLowerCase()) || 
    a.user_email.toLowerCase().includes(searchAppt.toLowerCase())
  );
  const currentAppts = filteredAppts.slice((pageAppt - 1) * ITEMS_PER_PAGE, pageAppt * ITEMS_PER_PAGE);
  const totalApptPages = Math.ceil(filteredAppts.length / ITEMS_PER_PAGE) || 1;

  const filteredVideos = videos.filter(v => v.title.toLowerCase().includes(searchVideo.toLowerCase()));
  const currentVideos = filteredVideos.slice((pageVideo - 1) * ITEMS_PER_PAGE, pageVideo * ITEMS_PER_PAGE);
  const totalVideoPages = Math.ceil(filteredVideos.length / ITEMS_PER_PAGE) || 1;

  const filteredBlogs = blogs.filter(b => b.title.toLowerCase().includes(searchBlog.toLowerCase()));
  const currentBlogs = filteredBlogs.slice((pageBlog - 1) * ITEMS_PER_PAGE, pageBlog * ITEMS_PER_PAGE);
  const totalBlogPages = Math.ceil(filteredBlogs.length / ITEMS_PER_PAGE) || 1;

  // Pagination UI Component
  const PaginationControls = ({ currentPage, totalPages, setPage }) => (
    <div className="flex justify-between items-center mt-8 border-t border-gray-100 pt-6">
      <span className="text-xs font-bold text-gray-400">Page {currentPage} of {totalPages}</span>
      <div className="flex gap-2">
        <button 
          onClick={() => setPage(p => Math.max(1, p - 1))} 
          disabled={currentPage === 1}
          className="px-4 py-2 border border-gray-200 rounded-lg text-xs font-bold disabled:opacity-50 hover:bg-gray-50 transition-all"
        >
          Previous
        </button>
        <button 
          onClick={() => setPage(p => Math.min(totalPages, p + 1))} 
          disabled={currentPage === totalPages}
          className="px-4 py-2 border border-gray-200 rounded-lg text-xs font-bold disabled:opacity-50 hover:bg-gray-50 transition-all"
        >
          Next
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-deep-charcoal font-sans selection:bg-ocean-teal selection:text-white pb-20 relative">
      
      {/* ================= CUSTOM TOAST NOTIFICATION ================= */}
      <div className={`fixed top-5 right-5 z-[200] transition-all duration-500 transform ${toast.show ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0 pointer-events-none'}`}>
        <div className={`px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 backdrop-blur-md ${toast.type === 'error' ? 'bg-red-500/90 text-white' : 'bg-ocean-teal/90 text-white'}`}>
          {toast.type === 'error' ? '⚠️' : '✅'}
          <span className="font-bold text-sm tracking-wide">{toast.message}</span>
        </div>
      </div>

      {/* ================= CUSTOM DELETE MODAL ================= */}
      {deleteModal.show && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-deep-charcoal/80 backdrop-blur-sm animate-fade-in">
          <div className="bg-white w-full max-w-sm rounded-[2rem] shadow-2xl p-8 border border-red-100 animate-scale-up text-center">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
            </div>
            <h2 className="text-xl font-serif font-bold text-deep-charcoal mb-2">Are you sure?</h2>
            <p className="text-sm text-gray-500 mb-8 leading-relaxed">This action cannot be undone. This item will be permanently removed.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteModal({ show: false, table: '', id: '' })} className="flex-1 bg-gray-50 hover:bg-gray-100 text-gray-600 font-bold py-3 rounded-xl transition-all">Cancel</button>
              <button onClick={executeDelete} className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold py-3 rounded-xl shadow-lg shadow-red-500/20 transition-all">Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ================= STICKY HEADER ================= */}
      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100 shadow-sm py-4 px-6 md:px-12 mb-10">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
             <div className="w-10 h-10 rounded-full bg-ocean-teal/10 flex items-center justify-center text-ocean-teal">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
             </div>
             <div>
                <h1 className="font-serif font-bold text-xl leading-none">Admin Workspace</h1>
                <span className="text-[9px] font-black uppercase tracking-[0.2em] text-ocean-teal">Secure Control Panel</span>
             </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 px-5 py-2.5 bg-gray-50 border border-gray-100 rounded-full font-bold text-xs tracking-widest text-gray-500 hover:bg-red-50 hover:text-red-500 hover:border-red-100 transition-all duration-300">
            Sign Out <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path></svg>
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        
        {/* ================= GLOWING STATS GRID ================= */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {[
            { label: 'Pending Requests', count: appointments.filter(a => a.status === 'pending').length, icon: '📅', color: 'bg-white', text: 'text-deep-charcoal' },
            { label: 'Published Articles', count: blogs.length, icon: '✍️', color: 'bg-white', text: 'text-deep-charcoal' },
            { label: 'Video Content', count: videos.length, icon: '🎥', color: 'bg-gradient-to-br from-deep-charcoal to-black', text: 'text-white' }
          ].map((stat, i) => (
            <div key={i} className={`${stat.color} p-8 rounded-[2.5rem] shadow-sm border border-gray-100 flex items-center justify-between group hover:-translate-y-1 hover:shadow-xl transition-all duration-500`}>
              <div>
                <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${stat.color.includes('charcoal') ? 'text-white/50' : 'text-gray-400'}`}>{stat.label}</p>
                <h3 className={`text-5xl font-serif font-bold ${stat.text}`}>{stat.count}</h3>
              </div>
              <div className="text-4xl opacity-20 group-hover:opacity-100 transition-opacity duration-500 group-hover:scale-110">{stat.icon}</div>
            </div>
          ))}
        </div>

        {/* ================= NAVIGATION ================= */}
        <div className="flex flex-wrap gap-2 mb-10">
          {['appointments', 'videos', 'blogs'].map(tab => (
            <button 
              key={tab} 
              onClick={() => setActiveTab(tab)}
              className={`px-8 py-3.5 rounded-2xl font-black uppercase text-[10px] tracking-[0.2em] transition-all duration-300 ${activeTab === tab ? 'bg-ocean-teal text-white shadow-lg shadow-ocean-teal/20' : 'bg-white text-gray-500 border border-gray-100 hover:border-ocean-teal/30'}`}
            >
              {tab.replace('appointments', 'Inquiries')}
            </button>
          ))}
        </div>

        {/* ================= MAIN CONTENT ================= */}
        {loading ? (
          <div className="flex justify-center items-center py-32">
             <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ocean-teal"></div>
          </div>
        ) : (
          <div>
            
            {/* APPOINTMENTS TAB */}
            {activeTab === 'appointments' && (
              <div className="bg-white rounded-[3rem] shadow-xl border border-gray-50 overflow-hidden">
                <div className="p-8 md:p-10 bg-gray-50/50 border-b border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div>
                    <h2 className="text-2xl font-serif font-bold">Session Inquiries</h2>
                    <p className="text-xs text-gray-500 mt-1">Manage and track client requests.</p>
                  </div>
                  <div className="flex gap-3 w-full md:w-auto">
                    <input 
                      type="text" 
                      placeholder="Search name or email..." 
                      value={searchAppt}
                      onChange={(e) => setSearchAppt(e.target.value)}
                      className="px-5 py-2.5 bg-white border border-gray-200 rounded-xl text-sm outline-none focus:border-ocean-teal flex-1"
                    />
                    <button onClick={fetchAppointments} className="w-10 h-10 flex-shrink-0 bg-white border border-gray-200 rounded-xl flex items-center justify-center text-gray-400 hover:text-ocean-teal hover:border-ocean-teal transition-all shadow-sm" title="Refresh">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
                    </button>
                  </div>
                </div>
                
                <div className="overflow-x-auto p-8 md:p-10">
                  <div className="space-y-4">
                    {currentAppts.length === 0 ? (
                      <p className="py-12 text-center font-serif text-lg text-gray-400 italic">No inquiries found.</p>
                    ) : (
                      currentAppts.map((apt) => (
                        <div key={apt.id} className={`p-6 rounded-3xl border transition-all ${apt.status === 'pending' ? 'bg-yellow-50/30 border-yellow-100' : apt.status === 'replied' ? 'bg-green-50/30 border-green-100' : 'bg-gray-50 border-gray-100'}`}>
                          <div className="flex flex-col md:flex-row gap-6">
                            <div className="md:w-1/4">
                              <div className="flex items-center gap-2 mb-2">
                                {apt.status === 'pending' && <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-[9px] font-black uppercase rounded-md tracking-widest">Pending</span>}
                                {apt.status === 'read' && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[9px] font-black uppercase rounded-md tracking-widest">Read</span>}
                                {apt.status === 'replied' && <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[9px] font-black uppercase rounded-md tracking-widest">Replied</span>}
                              </div>
                              <div className="font-serif font-bold text-xl text-deep-charcoal">{apt.user_name}</div>
                              <div className="text-xs font-bold text-ocean-teal mt-1">{apt.phone_number || 'No Phone'}</div>
                              <div className="text-[10px] text-gray-400 mt-1 uppercase tracking-widest">{apt.user_email}</div>
                            </div>
                            
                            <div className="md:w-1/4 border-l border-gray-200 pl-6">
                              <div className="inline-block px-3 py-1 bg-ocean-teal/10 text-ocean-teal rounded-lg text-xs font-bold mb-2">{apt.session_type || 'General'}</div>
                              <div className="text-sm font-black uppercase tracking-widest">{apt.session_date}</div>
                              <div className="text-xs text-gray-500 mt-1 font-bold">{apt.session_time} • {apt.duration || '45 Mins'}</div>
                            </div>
                            
                            <div className="md:w-2/4 border-l border-gray-200 pl-6 flex flex-col justify-between">
                              <p className="text-sm text-gray-600 italic line-clamp-3 leading-relaxed mb-4">
                                {apt.message ? `"${apt.message}"` : 'No additional notes provided.'}
                              </p>
                              <div className="flex flex-wrap gap-2 mt-auto">
                                {apt.status !== 'read' && <button onClick={() => handleUpdateStatus(apt.id, 'read')} className="px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-500 hover:text-white rounded-lg text-xs font-bold transition-all">Mark Read</button>}
                                {apt.status !== 'replied' && <button onClick={() => handleUpdateStatus(apt.id, 'replied')} className="px-3 py-1.5 bg-green-50 text-green-600 hover:bg-green-500 hover:text-white rounded-lg text-xs font-bold transition-all">Mark Replied</button>}
                                <button onClick={() => requestDelete('appointments', apt.id)} className="px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-500 hover:text-white rounded-lg text-xs font-bold transition-all ml-auto">Delete</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <PaginationControls currentPage={pageAppt} totalPages={totalApptPages} setPage={setPageAppt} />
                </div>
              </div>
            )}

            {/* VIDEOS TAB */}
            {activeTab === 'videos' && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className="lg:col-span-4 h-fit" ref={videoFormRef}>
                  <div className={`p-10 rounded-[3rem] shadow-2xl relative overflow-hidden transition-all duration-500 ${editingVideoId ? 'bg-ocean-teal text-white border-4 border-ocean-teal' : 'bg-deep-charcoal text-white border-4 border-gray-800'}`}>
                    <div className="absolute top-[-50%] right-[-50%] w-64 h-64 bg-white opacity-10 rounded-full blur-3xl"></div>
                    <div className="flex justify-between items-center mb-8 relative z-10">
                      <h2 className="text-2xl font-serif font-bold">{editingVideoId ? 'Edit Video' : 'Add Video'}</h2>
                      {editingVideoId && <button onClick={cancelVideoEdit} className="text-xs font-bold bg-white/20 hover:bg-white/30 px-3 py-1 rounded-full transition-all">Cancel</button>}
                    </div>
                    
                    <form onSubmit={handleAddOrUpdateVideo} className="space-y-6 relative z-10">
                      {['title', 'video_link', 'category'].map((field) => (
                        <div key={field}>
                          <label className="text-[10px] font-black uppercase tracking-widest text-white/50 block mb-2">{field.replace('_', ' ')}</label>
                          <input 
                            type={field === 'video_link' ? 'url' : 'text'} required
                            value={newVideo[field]}
                            onChange={(e) => setNewVideo({...newVideo, [field]: e.target.value})}
                            className="w-full bg-white/10 border border-white/20 p-4 rounded-xl focus:border-white focus:ring-1 focus:ring-white outline-none text-sm transition-all text-white placeholder-white/30"
                          />
                        </div>
                      ))}
                      <button type="submit" disabled={isSubmittingVideo} className={`w-full py-4 rounded-xl font-black uppercase tracking-[0.2em] text-xs shadow-lg transition-all disabled:opacity-50 ${editingVideoId ? 'bg-white text-ocean-teal hover:bg-gray-50' : 'bg-ocean-teal text-white hover:bg-ocean-teal/90'}`}>
                        {isSubmittingVideo ? 'Saving...' : (editingVideoId ? 'Update Video' : 'Publish Video')}
                      </button>
                    </form>
                  </div>
                </div>
                
                <div className="lg:col-span-8 bg-white p-10 rounded-[3rem] shadow-xl border border-gray-50">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                    <div>
                      <h2 className="text-2xl font-serif font-bold">Video Library</h2>
                      <p className="text-xs text-gray-500 mt-1">Drag and drop items to reorder them on the website.</p>
                    </div>
                    <input 
                      type="text" placeholder="Search videos..." value={searchVideo} onChange={(e) => setSearchVideo(e.target.value)}
                      className="px-5 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm outline-none focus:border-ocean-teal w-full sm:w-auto"
                    />
                  </div>

                  <div className="space-y-3">
                    {currentVideos.map((v, index) => (
                      <div 
                        key={v.id} 
                        draggable
                        onDragStart={() => (dragItem.current = index + (pageVideo - 1) * ITEMS_PER_PAGE)}
                        onDragEnter={() => (dragOverItem.current = index + (pageVideo - 1) * ITEMS_PER_PAGE)}
                        onDragEnd={() => handleSortEnd('videos', videos)}
                        onDragOver={(e) => e.preventDefault()}
                        className="p-5 bg-white border border-gray-200 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between group hover:shadow-lg transition-all cursor-grab active:cursor-grabbing"
                      >
                        <div className="flex items-center gap-4">
                          <div className="text-gray-300 cursor-grab">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8h16M4 16h16"></path></svg>
                          </div>
                          <div>
                            <span className="text-[9px] font-black bg-ocean-teal/10 text-ocean-teal px-2 py-0.5 rounded-full uppercase tracking-widest">{v.category}</span>
                            <h3 className="text-md font-serif font-bold mt-1 line-clamp-1">{v.title}</h3>
                          </div>
                        </div>
                        <div className="mt-4 sm:mt-0 flex gap-2 shrink-0 ml-10 sm:ml-0">
                          <button onClick={() => handleEditVideoClick(v)} className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-[10px] font-bold text-ocean-teal hover:bg-ocean-teal/10 transition-all">Edit</button>
                          <button onClick={() => requestDelete('videos', v.id)} className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-[10px] font-bold text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all">Delete</button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <PaginationControls currentPage={pageVideo} totalPages={totalVideoPages} setPage={setPageVideo} />
                </div>
              </div>
            )}

            {/* BLOGS TAB */}
            {activeTab === 'blogs' && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className="lg:col-span-5 h-fit" ref={blogFormRef}>
                  <div className={`p-10 rounded-[3rem] shadow-xl transition-all duration-500 ${editingBlogId ? 'bg-ocean-teal/5 border-2 border-ocean-teal' : 'bg-white border border-gray-100'}`}>
                    <div className="flex justify-between items-center mb-8">
                      <h2 className="text-2xl font-serif font-bold">{editingBlogId ? 'Edit Article' : 'Draft Article'}</h2>
                      {editingBlogId && <button onClick={cancelBlogEdit} className="text-xs font-bold text-gray-500 hover:text-deep-charcoal transition-colors">Cancel</button>}
                    </div>

                    <form onSubmit={handleAddOrUpdateBlog} className="space-y-5">
                      {['title', 'image_url'].map(field => (
                        <div key={field}>
                          <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 block mb-2">{field.replace('_', ' ')}</label>
                          <input type={field==='image_url'?'url':'text'} required value={newBlog[field]} onChange={(e)=>setNewBlog({...newBlog, [field]:e.target.value})} className="w-full p-4 rounded-xl bg-gray-50 border border-transparent focus:border-ocean-teal focus:bg-white focus:ring-2 focus:ring-ocean-teal/10 outline-none text-sm transition-all" />
                        </div>
                      ))}
                      <div>
                         <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 block mb-2">Summary (Excerpt)</label>
                         <textarea rows="3" required value={newBlog.excerpt} onChange={(e)=>setNewBlog({...newBlog, excerpt:e.target.value})} className="w-full p-4 rounded-xl bg-gray-50 border border-transparent focus:border-ocean-teal focus:bg-white focus:ring-2 focus:ring-ocean-teal/10 outline-none text-sm resize-none transition-all"></textarea>
                      </div>
                      <div>
                         <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 block mb-2">Full Content</label>
                         <textarea rows="8" required value={newBlog.content} onChange={(e)=>setNewBlog({...newBlog, content:e.target.value})} className="w-full p-4 rounded-xl bg-gray-50 border border-transparent focus:border-ocean-teal focus:bg-white focus:ring-2 focus:ring-ocean-teal/10 outline-none text-sm resize-none transition-all"></textarea>
                      </div>
                      <button type="submit" disabled={isSubmittingBlog} className={`w-full py-4 rounded-xl font-black uppercase tracking-widest text-xs shadow-xl active:scale-95 transition-all disabled:opacity-50 ${editingBlogId ? 'bg-ocean-teal text-white hover:bg-ocean-teal-dark' : 'bg-deep-charcoal text-white hover:bg-black'}`}>
                        {isSubmittingBlog ? 'Saving...' : (editingBlogId ? 'Update Article' : 'Publish Article')}
                      </button>
                    </form>
                  </div>
                </div>
                
                <div className="lg:col-span-7">
                  <div className="bg-white p-10 rounded-[3rem] shadow-xl border border-gray-100">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                      <div>
                        <h2 className="text-2xl font-serif font-bold">Published Library</h2>
                        <p className="text-xs text-gray-500 mt-1">Drag and drop to reorder articles.</p>
                      </div>
                      <input 
                        type="text" placeholder="Search articles..." value={searchBlog} onChange={(e) => setSearchBlog(e.target.value)}
                        className="px-5 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm outline-none focus:border-ocean-teal w-full sm:w-auto"
                      />
                    </div>

                    <div className="space-y-3">
                      {currentBlogs.map((b, index) => (
                        <div 
                          key={b.id} 
                          draggable
                          onDragStart={() => (dragItem.current = index + (pageBlog - 1) * ITEMS_PER_PAGE)}
                          onDragEnter={() => (dragOverItem.current = index + (pageBlog - 1) * ITEMS_PER_PAGE)}
                          onDragEnd={() => handleSortEnd('blogs', blogs)}
                          onDragOver={(e) => e.preventDefault()}
                          className={`p-5 rounded-2xl border transition-all flex flex-col sm:flex-row sm:items-center justify-between group cursor-grab active:cursor-grabbing ${editingBlogId === b.id ? 'bg-ocean-teal/5 border-ocean-teal/30' : 'bg-white border-gray-200 hover:shadow-lg'}`}
                        >
                          <div className="flex items-center gap-4 overflow-hidden mb-4 sm:mb-0">
                             <div className="text-gray-300">
                               <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8h16M4 16h16"></path></svg>
                             </div>
                             <img src={b.image_url} alt="cover" className="w-12 h-12 rounded-xl object-cover shadow-sm hidden md:block" />
                             <div className="flex-1 pr-4">
                                <span className="text-[9px] font-black uppercase tracking-widest text-gray-400">{new Date(b.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                <h3 className="text-md font-serif font-bold mt-0.5 leading-snug truncate">{b.title}</h3>
                             </div>
                          </div>
                          <div className="flex gap-2 shrink-0 ml-10 sm:ml-0">
                            <button onClick={() => handleEditBlogClick(b)} className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-[10px] font-bold text-ocean-teal hover:bg-ocean-teal/10 transition-all">Edit</button>
                            <button onClick={() => requestDelete('blogs', b.id)} className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-[10px] font-bold text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all">Delete</button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <PaginationControls currentPage={pageBlog} totalPages={totalBlogPages} setPage={setPageBlog} />
                  </div>
                </div>
              </div>
            )}
            
          </div>
        )}

      </div>
    </div>
  );
}